/**
 * One toast (BON-472). Owns its own auto-dismiss timer so the timers are
 * independent — a second toast arriving must not extend or cut short the first.
 */
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useEffect } from 'react';

import { DEFAULT_TOAST_MS, type Toast, type ToastTone } from './toastContext';

const TONE_CLASS: Record<ToastTone, string> = {
  success:
    'border-moss-300 bg-moss-50 text-moss-900 dark:border-moss-700 dark:bg-moss-900/40 dark:text-moss-100',
  error:
    'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/40 dark:text-red-100',
  info: 'border-stone-300 bg-white text-bark-900 dark:border-stone-600 dark:bg-[#1e2520] dark:text-stone-100',
};

const TONE_ICON: Record<ToastTone, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};

export function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const tone = toast.tone ?? 'success';
  const Icon = TONE_ICON[tone];

  useEffect(() => {
    const ms = toast.durationMs ?? DEFAULT_TOAST_MS;
    const timer = window.setTimeout(onDismiss, ms);
    return () => window.clearTimeout(timer);
    // Re-arming on every `onDismiss` identity change would restart the clock on
    // an unrelated parent render, so the timer is keyed on the toast alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [toast.id, toast.durationMs]);

  return (
    <div
      className={`pointer-events-auto flex w-80 items-start gap-2.5 rounded-xl border px-3.5 py-3 shadow-lg ${TONE_CLASS[tone]}`}
    >
      <Icon size={17} className="mt-0.5 flex-shrink-0" aria-hidden />
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="flex-shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
      >
        <X size={15} aria-hidden />
      </button>
    </div>
  );
}
